/* @flow */

import React from 'react';

import LoadingComponent from 'components/loading';
import TwoFactorSetupViewComponent from './view';

import UserService from 'services/UserService';

export const STEP_SELECT = 'select';
export const STEP_QR = 'qr';
export const STEP_SMS_MOBILE = 'sms-mobile';
export const STEP_SMS_CODE = 'sms-code';
export const STEP_DONE = 'done';

type TwoFactorSetupComponentProps = {
  onComplete?: () => void;
  onCancel?: () => void;
  cancelLabelId?: string;
}

type TwoFactorSetupComponentState = {
  isLoading: boolean;
  isProcessing: boolean;
  step: string;
  smsSelected: boolean;
  qrSelected: boolean;
  qrActivated: boolean;
  smsActivated: boolean;
  qrCodeUrl: ?string;
  qrPin: string;
  wrongQrPin: boolean;
  mobile: string;
  smsCode: string;
  wrongSmsCode: boolean;
  smsError: ?string;
  queue: string[];
  queueIndex: number;
}

class TwoFactorSetupComponent extends React.Component<TwoFactorSetupComponentProps, TwoFactorSetupComponentState> {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isProcessing: false,
      step: STEP_SELECT,
      smsSelected: false,
      qrSelected: false,
      qrActivated: false,
      smsActivated: false,
      qrCodeUrl: null,
      qrPin: '',
      wrongQrPin: false,
      mobile: '',
      smsCode: '',
      wrongSmsCode: false,
      smsError: null,
      queue: [],
      queueIndex: 0
    };
  }

  componentDidMount = async () => {
    try {
      const state = await UserService.getTwoFactorState();
      this.setState({
        isLoading: false,
        smsSelected: state.smsSelected,
        qrSelected: state.qrSelected,
        qrActivated: state.qrActivated,
        smsActivated: state.smsActivated,
        mobile: state.mobile ?? ''
      });
    } catch (e) {
      console.error(e);
      this.setState({ isLoading: false });
    }
  };

  handleSmsSelectedChange = (value: boolean) => this.setState({ smsSelected: value });
  handleQrSelectedChange = (value: boolean) => this.setState({ qrSelected: value });
  handleQrPinChange = (value: string) => this.setState({ qrPin: value });
  handleMobileChange = (value: string) => this.setState({ mobile: value });
  handleSmsCodeChange = (value: string) => this.setState({ smsCode: value });

  validateSelection = async () => {
    if (!this.state.smsSelected && !this.state.qrSelected) return;
    this.setState({ isProcessing: true });

    try {
      await UserService.setTwoFactorSelected(this.state.smsSelected, this.state.qrSelected);

      const queue = [];
      if (this.state.qrSelected && !this.state.qrActivated) queue.push(STEP_QR);
      if (this.state.smsSelected && !this.state.smsActivated) queue.push(STEP_SMS_MOBILE);

      this.setState({ queue, queueIndex: 0, isProcessing: false });
      await this.goToStep(queue.length > 0 ? queue[0] : null);
    } catch (e) {
      console.error(e);
      this.setState({ isProcessing: false });
    }
  };

  goToStep = async (step: ?string) => {
    if (step === null) {
      this.complete();
      return;
    }

    if (step === STEP_QR) {
      const [qrCodeUrl] = await UserService.getQRCode();
      this.setState({ step: STEP_QR, qrCodeUrl, qrPin: '', wrongQrPin: false });
    } else if (step === STEP_SMS_MOBILE) {
      this.setState({ step: STEP_SMS_MOBILE, smsError: null });
    }
  };

  advanceQueue = async () => {
    const queueIndex = this.state.queueIndex + 1;
    this.setState({ queueIndex });
    if (queueIndex >= this.state.queue.length) {
      this.complete();
    } else {
      await this.goToStep(this.state.queue[queueIndex]);
    }
  };

  complete = () => {
    if (this.props.onComplete) {
      this.props.onComplete();
    } else {
      this.setState({ step: STEP_DONE });
    }
  };

  validateQr = async () => {
    this.setState({ isProcessing: true });
    try {
      await UserService.validateTwoFactor(this.state.qrPin);
      this.setState({ isProcessing: false, wrongQrPin: false });
      await this.advanceQueue();
    } catch (e) {
      this.setState({ isProcessing: false, wrongQrPin: true });
    }
  };

  sendSmsCode = async () => {
    this.setState({ isProcessing: true, smsError: null });
    try {
      await UserService.updateMobile({ mobile: this.state.mobile });
      await UserService.sendSmsCode();
      this.setState({ isProcessing: false, step: STEP_SMS_CODE, smsCode: '', wrongSmsCode: false });
    } catch (e) {
      this.setState({ isProcessing: false, smsError: 'twofactor.sms.senderror' });
    }
  };

  validateSmsCode = async () => {
    this.setState({ isProcessing: true });
    try {
      await UserService.validateSmsCode(this.state.smsCode);
      this.setState({ isProcessing: false, wrongSmsCode: false });
      await this.advanceQueue();
    } catch (e) {
      this.setState({ isProcessing: false, wrongSmsCode: true });
    }
  };

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    return (
      <TwoFactorSetupViewComponent
        step={this.state.step}
        isProcessing={this.state.isProcessing}
        smsSelected={this.state.smsSelected}
        qrSelected={this.state.qrSelected}
        qrCodeUrl={this.state.qrCodeUrl}
        qrPin={this.state.qrPin}
        wrongQrPin={this.state.wrongQrPin}
        mobile={this.state.mobile}
        smsCode={this.state.smsCode}
        wrongSmsCode={this.state.wrongSmsCode}
        smsError={this.state.smsError}
        cancelLabelId={this.props.cancelLabelId}
        onCancel={this.props.onCancel}
        handleSmsSelectedChange={this.handleSmsSelectedChange}
        handleQrSelectedChange={this.handleQrSelectedChange}
        handleQrPinChange={this.handleQrPinChange}
        handleMobileChange={this.handleMobileChange}
        handleSmsCodeChange={this.handleSmsCodeChange}
        validateSelection={this.validateSelection}
        validateQr={this.validateQr}
        sendSmsCode={this.sendSmsCode}
        validateSmsCode={this.validateSmsCode}
      />
    );
  }
}

export default TwoFactorSetupComponent;
